import { createContext, useContext, ReactNode, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useSessionContext } from '@supabase/auth-helpers-react';

import { supabase } from '@/lib/supabase';
import { USER_EVENTS, useTracking } from '@/hooks/useTracking';
import { usePlayBilling } from '@/hooks/usePlayBilling';
import { isTwaOrPlayStoreInstalled } from '@/lib/detection';
import { debounce } from '@/lib/utils';

interface GemBalance {
  monthly: number;
  purchased: number;
  total: number;
}

interface GemBalanceContextType {
  balance: GemBalance;
  loading: boolean;
  error: string | null;
  getRefreshedBalance: () => Promise<GemBalance | undefined>;
  refreshBalance: () => void;
}

interface GemBalanceResponse {
  totalBalance: number;
  balances: {
    gems_monthly_balance: number;
    gems_purchased_balance: number;
  };
  hasSufficientBalance?: boolean;
  cost?: number;
}

// -1 total means unknown / not applicable
export const DEFAULT_BALANCE = { monthly: 0, purchased: 0, total: -1 };

const GemBalanceContext = createContext<GemBalanceContextType | undefined>(undefined);

export const GemBalanceProvider = ({ children }: { children: ReactNode }) => {
  const { session } = useSessionContext();
  const [balance, setBalance] = useState<GemBalance>(DEFAULT_BALANCE);
  const [loading, setLoading] = useState(true);
  const isInitRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const { trackEvent } = useTracking();
  const { checkExistingPurchases } = usePlayBilling();

  /**
   * Refresh gem balance with fresh user from supabase
   * @returns new balance or undefined if no change
   */
  const refreshBalance = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (await isTwaOrPlayStoreInstalled()) {
      await checkExistingPurchases({ userId: session?.user?.id });
    }

    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session?.user) {
        setBalance(DEFAULT_BALANCE);
        return DEFAULT_BALANCE;
      }

      const { data: balanceData, error: balanceError } = await supabase.functions.invoke<GemBalanceResponse>('eh-user-balance', {
        method: 'GET'
      });
      if (balanceError) console.error('Error refreshing gem balance:', balanceError);
      if (!balanceData || !('totalBalance' in balanceData)) throw new Error('Failed to refresh gem balance');

      const { totalBalance, balances } = balanceData;
      const monthlyBalance = balances.gems_monthly_balance ?? 0;
      const purchasedBalance = balances.gems_purchased_balance ?? 0;

      const newBalance = {
        monthly: monthlyBalance,
        purchased: purchasedBalance,
        total: totalBalance,
      };
      
      setBalance(newBalance);
      return newBalance;
    } catch (err) {
      setError('Failed to refresh gem balance');
      trackEvent(USER_EVENTS.USER_ERROR, {
        error: `Failed to refresh gem balance: ${err instanceof Error ? err.message : JSON.stringify(err)}`,
        source: 'useGemBalance',
      });
    } finally {
      setLoading(false);
    }
    // omit trackEvent dependency to avoid useless re-rendering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkExistingPurchases]);

  const debouncedRefreshBalance = useMemo(() => debounce(refreshBalance, 500), [refreshBalance]);

  useEffect(() => {
    if (!session?.user) {
      setBalance(DEFAULT_BALANCE);
      setLoading(false);
      return;
    }
    // init only once
    if (isInitRef.current) return;
    void refreshBalance();
    isInitRef.current = true;
  }, [refreshBalance, session?.user]);

  const value = {
    balance,
    loading,
    error,
    getRefreshedBalance: refreshBalance, // bypass debounce and returns fresh balance
    refreshBalance: debouncedRefreshBalance,
  };

  return (
    <GemBalanceContext.Provider value={value}>
      {children}
    </GemBalanceContext.Provider>
  );
};

export const useGemBalance = () => {
  const context = useContext(GemBalanceContext);
  
  if (context === undefined) {
    throw new Error('useGemBalance must be used within a GemBalanceProvider');
  }
  
  return context;
}; 